/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BEM, useTranslate } from "@adapptio/lib-components";
import { Panel, PANEL_STYLE, PanelNote } from "../../../../../ui/components/Panel";
import { ConfigEditorEdit } from "../../ConfigEditorEdit";
import { InspectorContextProvider } from "../../../../../inspector/InspectorContext";
import { Inspector } from "../../../../../inspector";
import { useStateWithEvent } from "../../../../../ui/hooks";
import { useSimpleEvent } from "../../../../../ui/hooks/useSimpleEvent";
import { CommandButton } from "../../../../../ui/components/Button/CommandButton";
import { DEFAULT_ENV_NAME } from "../../../Common/ConfigModel";

import { InspectPanel, InspectPanelContent, InspectPanelHeader, InspectPanelHeaderInfo, InspectPanelHeaderInfoTitle } from "../../../../../ui/components/InspectPanel";
import { FieldRow } from "../../../../../ui/components/Fields/FieldRow";

export interface IConfigEditorEditContentProps {
	className?: string;
	view: ConfigEditorEdit;
}

export const ConfigEditorEditContent: React.FC<IConfigEditorEditContentProps> = ({ className, view }) => {
	const baseClass = BEM("edt-config-editor-edit-content");
	const t = useTranslate();

	const inspectorElRef = useRef();
	const configModel = view.getConfigModel();

	// Categories are static from manifest schema
	const categories = useMemo(() => view.getCategories(), [ view ]);

	// Selected category - updates on selection change
	const selectedCategory = useStateWithEvent(
		view.onSelectionChange,
		() => view.getSelectedCategory(),
		[ view ]
	);

	// Get content nodes from model
	const getSelectedContent = useCallback(() => {
		const configOpts = configModel.getOptsForEnvironment(DEFAULT_ENV_NAME);
		if (!configOpts) {
			return [];
		}
		return configOpts.schema.getPropGroupNodes(configOpts, selectedCategory) || [];
	}, [ configModel, selectedCategory ]);

	// State for selected content
	const [ selectedContent, setSelectedContent ] = useState(getSelectedContent);

	// Update content when category changes
	useEffect(() => {
		setSelectedContent(getSelectedContent());
	}, [ getSelectedContent ]);

	// Update content when model changes
	useSimpleEvent(
		configModel.onModelChange,
		() => setSelectedContent(getSelectedContent()),
		[ configModel, getSelectedContent ]
	);

	const publicKey = useMemo(() => view.getPublicKey(), [ view ]);

	if (className) {
		baseClass.add(className);
	}

	const categoryTitle = useMemo(() => {
		return t("editor", categories.find(cat => cat.id === selectedCategory)?.label)
	}, [ selectedCategory, categories, t ]);

	return (
		<Panel style={PANEL_STYLE.PRIMARY} scroll className={baseClass.toClassName()}>
			<div className={baseClass.element("wrapper").toClassName()}>
				{selectedCategory === "ssh" && (
					<InspectPanel>
						<InspectPanelHeader>
							<InspectPanelHeaderInfo>
								<InspectPanelHeaderInfoTitle>SSH Public Key</InspectPanelHeaderInfoTitle>
							</InspectPanelHeaderInfo>
						</InspectPanelHeader>
						<InspectPanelContent>
							{publicKey
								? (
									<FieldRow>
										<div className={baseClass.element("ssh-content").toClassName()}>
											<CommandButton
												icon="mdi/content-copy"
												baseClass={baseClass.element("copy-button")}
												commandId="appConfig.publicKey.copy"
												commandArgs={[ publicKey ]}
												tooltip="Copy"
											/>
											{publicKey}
										</div>
									</FieldRow>
								): (
									<PanelNote>Public key is not configured</PanelNote>
								)}
						</InspectPanelContent>
					</InspectPanel>
				)}
				{selectedCategory !== "ssh" && (
					<div className={baseClass.element("inspector").toClassName()} ref={inspectorElRef}>
						<InspectorContextProvider inspectorElRef={inspectorElRef}>
							<InspectPanel>
								<InspectPanelHeader>
									<InspectPanelHeaderInfo>
										<InspectPanelHeaderInfoTitle>{categoryTitle}</InspectPanelHeaderInfoTitle>
									</InspectPanelHeaderInfo>
								</InspectPanelHeader>
								<InspectPanelContent>
									{selectedContent.map((model) => (
										<Inspector
											key={model.nodeId}
											model={model}
										/>
									))}
								</InspectPanelContent>
							</InspectPanel>
						</InspectorContextProvider>
					</div>
				)}
			</div>
		</Panel>
	);
};
